import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  Users, Plus, Trash2, Pencil, RefreshCw, Ban, ShieldOff, CheckCircle2,
} from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";
import { useAuth } from "@/lib/auth";
import {
  TIER_TONE, LICENSE_LABEL,
  TechProfileFormModal, TechProfileDeleteConfirm, arrToCsv,
} from "@/components/TechProfileFormModal";
import TechProfileCustomFieldsPanel from "@/components/TechProfileCustomFieldsPanel";
import { AltKeyBadge, RateOverrideBadge, useAltKeyStatus } from "@/components/AltKeyBadge";

// ============================================================================
// Platform-admin Tech Profiles CRUD (2026-07-31, refactored). Owner also has
// access to this page via widened ProtectedRoute; call_center + owner use the
// tenant-scoped page at /:slug/app/tech-profiles which shares the same form
// + delete-confirm modals via `@/components/TechProfileFormModal`.
// ============================================================================

const LABEL_CLS = "text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold";


export default function AdminTechProfiles() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "platform_admin";
  const [tenants, setTenants] = useState([]);
  const [tenantSlug, setTenantSlug] = useState("");
  const [tenantsLoad, setTenantsLoad] = useState({ status: "loading", error: null });

  const loadTenants = useCallback(async () => {
    setTenantsLoad({ status: "loading", error: null });
    if (!isPlatformAdmin) {
      try {
        const { data } = await api.get("/tenants/current");
        if (data?.slug) {
          setTenants([{ slug: data.slug, business_name: data.business_name || data.slug }]);
          setTenantSlug(data.slug);
          setTenantsLoad({ status: "ok", error: null });
        } else {
          throw new Error("Tenant lookup returned no slug");
        }
      } catch (err) {
        setTenants([]);
        setTenantsLoad({ status: "error", error: extractApiError(err) });
      }
      return;
    }
    try {
      const { data } = await api.get("/tenants");
      const rows = (Array.isArray(data) ? data : []).filter((t) => t.slug);
      setTenants(rows);
      setTenantsLoad({ status: "ok", error: null });
      if (!tenantSlug && rows.length) setTenantSlug(rows[0].slug);
    } catch (err) {
      setTenants([]);
      setTenantsLoad({ status: "error", error: extractApiError(err) });
    }
  }, [tenantSlug, isPlatformAdmin]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  return (
    <div data-testid="admin-tech-profiles-page" className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[.15em] text-[color:var(--cc-ink-muted)] font-semibold">
            {isPlatformAdmin ? "Platform admin" : "Admin"}
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1 inline-flex items-center gap-2.5">
            <Users size={22} strokeWidth={1.6} className="text-[#0072bc]" />
            Tech Profiles
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Human-entered technician roster. Drives Dispatch Assist tiering, license gates, and territory routing.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isPlatformAdmin ? (
            <>
              <label className={LABEL_CLS}>Tenant</label>
              <select
                data-testid="tech-profiles-tenant-select"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                className="border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] bg-white outline-none focus:border-[#0072bc] min-w-[220px]"
              >
                {tenants.map((t) => (
                  <option key={t.slug} value={t.slug}>{t.business_name} · /{t.slug}</option>
                ))}
              </select>
            </>
          ) : (
            <span className="text-[12.5px] text-[color:var(--cc-ink-muted)]" data-testid="tech-profiles-tenant-label">
              Tenant: <b className="text-[#0e1420]">{tenants[0]?.business_name || tenantSlug}</b>
            </span>
          )}
        </div>
      </header>

      {!tenantSlug ? (
        <PanelState
          status={tenantsLoad.status} error={tenantsLoad.error}
          isEmpty={tenants.length === 0} emptyTitle="No tenants available"
          emptyBody="Provision a tenant first." testIdPrefix="tech-profiles-tenants"
        />
      ) : (
        <ProfilesTable tenantSlug={tenantSlug} />
      )}
    </div>
  );
}


function ProfilesTable({ tenantSlug }) {
  const [rows, setRows] = useState([]);
  const [load, setLoad] = useState({ status: "loading", error: null });
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const altKey = useAltKeyStatus(tenantSlug);

  const load_ = useCallback(async () => {
    setLoad({ status: "loading", error: null });
    try {
      const { data } = await api.get("/tech-profiles", { params: { tenant_slug: tenantSlug } });
      setRows(Array.isArray(data) ? data : []);
      setLoad({ status: "ok", error: null });
    } catch (err) {
      setRows([]);
      setLoad({ status: "error", error: extractApiError(err) });
    }
  }, [tenantSlug]);

  useEffect(() => { load_(); }, [load_]);

  const visible = rows.filter((r) => showInactive ? true : r.active !== false);

  return (
    <>
      <TechProfileCustomFieldsPanel tenantSlug={tenantSlug} />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 text-[12.5px]">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="tech-profiles-toggle-inactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="accent-[#0072bc]"
            />
            Show inactive
          </label>
          <span className="text-[color:var(--cc-ink-muted)]">
            {visible.length} of {rows.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load_}
            data-testid="tech-profiles-refresh"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 border border-[#e7e9ee] rounded-lg hover:bg-[#f5f6f8]"
          >
            <RefreshCw size={14} strokeWidth={1.8} /> Refresh
          </button>
          <button
            onClick={() => setEditing({})}
            data-testid="tech-profiles-new-btn"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 bg-[#0072bc] text-white rounded-lg hover:bg-[#005a95]"
          >
            <Plus size={14} strokeWidth={2} /> New tech profile
          </button>
        </div>
      </div>

      {load.status !== "ok" || visible.length === 0 ? (
        <PanelState
          status={load.status} error={load.error}
          isEmpty={visible.length === 0}
          emptyTitle="No tech profiles yet"
          emptyBody='Click "New tech profile" to add your first technician.'
          testIdPrefix="tech-profiles"
        />
      ) : (
        <div className="border border-[#e7e9ee] rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-[13px] min-w-[900px]">
            <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold">Name</th>
                <th className="text-left px-3 py-2.5 font-semibold">Tier</th>
                <th className="text-left px-3 py-2.5 font-semibold">License</th>
                <th className="text-left px-3 py-2.5 font-semibold">Email</th>
                <th className="text-left px-3 py-2.5 font-semibold">Territory</th>
                <th className="text-left px-3 py-2.5 font-semibold">Avoid tags</th>
                <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                <th className="text-right px-3 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} data-testid={`tech-profile-row-${r.id}`} className="border-t border-[#e7e9ee]">
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-[#0e1420]">{r.name}</div>
                    <AltKeyBadge status={altKey[r.id]} testId={`tech-profile-altkey-${r.id}`} />
                    <RateOverrideBadge pct={r.fixed_commission_pct} testId={`tech-profile-rate-override-${r.id}`} />
                    {r.communication_style && (
                      <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5 truncate max-w-[240px]">
                        {r.communication_style}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] rounded-full border font-semibold uppercase tracking-wider ${TIER_TONE[r.tier] || "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]"}`}>
                      {r.tier}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">{LICENSE_LABEL[r.license] || r.license}</td>
                  <td className="px-3 py-2.5 text-[12px]" data-testid={`tech-profile-email-${r.id}`}>
                    {r.email ? (
                      <a href={`mailto:${r.email}`} className="text-[#0072bc] hover:underline">{r.email}</a>
                    ) : (
                      r.active !== false ? (
                        <span className="text-[#7a1c1c] font-semibold" title="Required for active techs">missing</span>
                      ) : (
                        <span className="text-[color:var(--cc-ink-muted)]">—</span>
                      )
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[12.5px]">
                    <div>{arrToCsv(r.home_territory) || "—"}</div>
                    {r.cross_territory && (
                      <div className="text-[11px] text-[#0072bc] font-semibold mt-0.5">cross-territory OK</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {(r.avoid_tags || []).length === 0 ? (
                      <span className="text-[color:var(--cc-ink-muted)]">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(r.avoid_tags || []).map((t) => (
                          <span key={t} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-full bg-[#fff5f5] text-[#7a1c1c] border border-[#f5c2c2]">
                            <Ban size={10} strokeWidth={2} /> {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {r.active !== false ? (
                      <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0e8a68]">
                        <CheckCircle2 size={13} strokeWidth={2} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[color:var(--cc-ink-muted)]">
                        <ShieldOff size={13} strokeWidth={2} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setEditing(r)}
                        data-testid={`tech-profile-edit-${r.id}`}
                        className="p-1.5 hover:bg-[#f5f6f8] rounded-md"
                        title="Edit"
                      >
                        <Pencil size={14} strokeWidth={1.8} />
                      </button>
                      <button
                        onClick={() => setConfirmingDelete(r)}
                        data-testid={`tech-profile-delete-${r.id}`}
                        className="p-1.5 hover:bg-[#fff5f5] hover:text-[#7a1c1c] rounded-md"
                        title="Delete"
                      >
                        <Trash2 size={14} strokeWidth={1.8} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TechProfileFormModal
          tenantSlug={tenantSlug}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load_(); }}
        />
      )}
      {confirmingDelete && (
        <TechProfileDeleteConfirm
          tenantSlug={tenantSlug}
          profile={confirmingDelete}
          onClose={() => setConfirmingDelete(null)}
          onDeleted={async () => { setConfirmingDelete(null); await load_(); }}
        />
      )}
    </>
  );
}
